import { checkAuth } from "../../api";
import React, { useState, useEffect } from "react";
import { CheckIcon } from "@heroicons/react/20/solid";
import {Badge, Flex, Title} from "@mantine/core";

import { usePricingDetailsList } from "../../hooks/usePricingDetails";
import {
  nonProtectedRoutes,
  protectedRoutes,
} from "../../utils/nonProtectedRoutingPaths";

import {getFormattedPrice, getRequestLimit} from "../../utils/pricing";
import { PricingModal } from "./components/PricingModal";
import styles from "./PricingTable.module.css";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const PricingTable = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [billing, setBilling] = useState("monthly_price");
  const [showModal, setShowModal] = useState(false);

  const { pricing } = usePricingDetailsList();

  useEffect(() => {
    const verifyAuth = async () => {
      const authStatus = await checkAuth();
      setIsAuthenticated(authStatus.isValid);
      setIsLoading(false);

      // Show modal immediately if user is not authenticated
      if (!authStatus.isValid) {
        setShowModal(true);
      }
    };
    verifyAuth();
  }, []);
  const billingParam = billing.replace("_price", "")
  const billing_price = billing === "annual_price" ? "monthly_annual_price" : billing;
  const tiers = [
    {
      name: "Basic",
      id: "tier-basic",
      href: isAuthenticated
        ? `/app/${protectedRoutes.CHECKOUT}?plan=basic&billing=${billingParam}`
        : nonProtectedRoutes.REGISTER,
      price: getFormattedPrice(pricing?.basic?.[billing_price]),
      annualPrice: getFormattedPrice(pricing?.basic?.annual_price),
      annualDiscount: pricing?.basic?.annual_discount,
      description:
        "For lawyers getting acquainted with AI-powered legal work—streamline drafting and automate routine tasks with ease.",
      features: [
        "Full Access to Email-based AI Assistant",
        "AI-Powered Document Drafting & Processing",
        "Access to Standard Legal Templates",
        "Secure Cloud Storage",
        getRequestLimit(pricing?.basic?.monthly_request_limit),
      ],
      featured: false,
    },
    {
      name: "Professional",
      id: "tier-professional",
      href: isAuthenticated
        ? `/app/${protectedRoutes.CHECKOUT}?plan=pro&billing=${billingParam}`
        : nonProtectedRoutes.REGISTER,
      price: getFormattedPrice(pricing?.pro?.[billing_price]),
      annualPrice: getFormattedPrice(pricing?.pro?.annual_price),
      annualDiscount: pricing?.pro?.annual_discount,
      description:
        "For lawyers handling high-volume work who want the power of AI with seamless integrations and partner tools.",
      features: [
        "Everything in Basic, plus:",
        "Litera Compare for High-Fidelity Comparisons",
        "Priority Email Support",
        getRequestLimit(pricing?.pro?.monthly_request_limit),
      ],
      featured: true,
    },
    {
      name: "Enterprise",
      id: "tier-enterprise",
      href: nonProtectedRoutes.CONTACT,
      price: "Contact Sales",
      description: "For Large Law Firms & Lawyers Seeking Custom AI Solutions.",
      features: [
        "All Pro features, plus:",
        "Custom Template Creation",
        "Bespoke Workflow Integrations",
        "Dedicated Account Management",
        "AI Adoption & Integration Services",
        "Enhanced Admin & Security Controls",
        getRequestLimit(pricing?.enterprise?.monthly_request_limit),
      ],
      featured: false,
    },
  ];

  return (
    <div className="relative isolate bg-white px-6 py-24 sm:py-32 lg:px-8">
      <div
        aria-hidden="true"
        className="absolute inset-x-0 -top-3 -z-10 transform-gpu overflow-hidden px-36 blur-3xl"
      >
      <div
        style={{
          clipPath:
            "polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)",
        }}
        className="mx-auto aspect-1155/678 w-[72.1875rem] bg-gradient-to-tr from-[#aa9664] via-[#b89d6f] to-[#8d7951] opacity-30"
      />
      </div>
      <div className="mx-auto max-w-4xl text-center">
        <Title order={1} c="var(--primary-color)" mb="sm">
          PRICING
        </Title>

        <p className="mt-2 text-5xl font-semibold tracking-tight text-balance text-gray-600 sm:text-3xl">
          Find the Perfect Plan for Your Practice
        </p>
      </div>
      <p className="mx-auto mt-6 max-w-2xl text-center text-lg font-medium text-pretty text-gray-600 sm:text-xl/8">
        Find the right plan to streamline your legal workflows, enhance
        efficiency, and improve accuracy—powered by AI. Whether you're a solo
        practitioner or a large firm, Socius adapts to your needs. Need help
        choosing? <br /> <a href="/contact">Contact us</a> for guidance
      </p>

      <div className="flex justify-center mb-6 m-5">
        <div className="w-48 relative bg-gray-200 rounded-full p-1">
          <div
          style={{backgroundColor: "var(--primary-color)"}}
            className={`absolute inset-y-0 left-0  rounded-full w-1/2 transition-transform duration-300 ease-in-out ${
              billing === "annual_price" ? "translate-x-full" : ""
            }`}
          ></div>
          <button
            onClick={() => setBilling("monthly_price")}
            className="relative w-1/2 text-center px-4 py-2 focus:outline-none"
          >
            <span
              className={
                billing === "monthly_price" ? "text-white" : "text-gray-600"
              }
            >
              Monthly
            </span>
          </button>
          <button
            onClick={() => setBilling("annual_price")}
            className="relative w-1/2 text-center px-4 py-2 focus:outline-none"
          >
            <span
              className={
                billing === "annual_price" ? "text-white" : "text-gray-600"
              }
            >
              Annual
            </span>
          </button>
        </div>
      </div>

      <div className="mx-auto mt-16 grid max-w-lg grid-cols-1 items-center gap-y-6 sm:mt-20 sm:gap-y-0 lg:max-w-6xl lg:grid-cols-3">
        {tiers.map((tier, tierIdx) => (
          <div
            key={tier.id}
            className={classNames(
              tier.featured
                ? `relative bg-gray-900 shadow-2xl ${
                    tierIdx === 1
                      ? "z-10 transform scale-105 lg:-translate-y-6"
                      : ""
                  }`
                : "bg-white/60 sm:mx-8 lg:mx-0",
              !tier.featured &&
                (tierIdx === 0
                  ? "lg:rounded-tl-3xl lg:rounded-bl-3xl"
                  : tierIdx === 2
                    ? "lg:rounded-tr-3xl lg:rounded-br-3xl"
                    : ""),
              "rounded-3xl p-8 ring-1 ring-gray-900/10 sm:p-10",
            )}
          >
            <h3
              id={tier.id}
              className={classNames(
                tier.featured ? styles.textGold : styles.textGoldDark,
                "text-base/7 font-semibold",
              )}
            >
              {tier.name}
            </h3>
            <p className="mt-4 flex items-end justify-between gap-x-2">
              <Flex gap="xs" align="end">
                <span
                  className={classNames(
                    tier.featured ? "text-white" : "text-gray-900",
                    "text-5xl font-semibold tracking-tight",
                  )}
                >
                  {tier.price}
                </span>
                {tier.price !== "Contact Sales" && (
                  <Flex direction="column">
                    {billing === "annual_price" && (
                      <Badge variant="filled" color={tier.featured ? "var(--accent-gold)" : "var(--accent-gold-hover)"}>
                        {tier.annualDiscount}% off
                      </Badge>
                    )}
                    <span
                      className={classNames(
                        tier.featured ? "text-gray-400" : "text-gray-500",
                        "text-base",
                      )}
                    >
                      /month
                    </span>
                  </Flex>
                )}
              </Flex>
              {billing === "annual_price" && tier.price !== "Contact Sales" && (
                <span
                  className={classNames(
                    tier.featured ? "text-white" : "text-gray-900",
                    "text-base",
                  )}
                >
                  {tier.annualPrice}/year
                </span>
              )}
            </p>
            <p
              className={classNames(
                tier.featured ? "text-gray-300" : "text-gray-600",
                "mt-6 text-base/7",
              )}
            >
              {tier.description}
            </p>
            <ul
              role="list"
              className={classNames(
                tier.featured ? "text-gray-300" : "text-gray-600",
                "mt-8 space-y-3 text-sm/6 sm:mt-10",
              )}
            >
              {tier.features.map((feature) => (
                <li key={feature} className="flex gap-x-3">
                  <CheckIcon
                    aria-hidden="true"
                    className={classNames(
                      tier.featured ? styles.textGold : styles.textGoldDark,
                      "h-6 w-5 flex-none",
                    )}
                  />
                  {feature}
                </li>
              ))}
            </ul>
            <a
              href={tier.href}
              aria-describedby={tier.id}
              className={classNames(
                tier.featured
                  ? `${styles.goldBg} text-white shadow-xs`
                  : `${styles.textGoldDark} ring-1 ${styles.ringGold} ring-inset`,
                "mt-8 block rounded-md px-3.5 py-2.5 text-center text-sm font-semibold focus-visible:outline-2 focus-visible:outline-offset-2 sm:mt-10",
              )}
            >
              Get started today
            </a>
          </div>
        ))}
      </div>

      <PricingModal opened={showModal} onClose={() => setShowModal(false)} />
    </div>
  );
};
